import React from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { cn } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import FullScreenModal from '../../../components/ui/Modal';
import { AttendanceStatus } from '../../attendance/types/attendance.types';

export interface StudentAttendanceHistoryEntry {
  id: string;
  date: string;
  lessonTitle: string;
  lessonNumber: number | null;
  notes: string;
  status: AttendanceStatus;
  presentCount: number;
  enrolledCount: number;
}

interface StudentAttendanceHistoryModalProps {
  open: boolean;
  studentName?: string;
  entries: StudentAttendanceHistoryEntry[];
  summary: {
    present: number;
    absent: number;
  };
  onClose: () => void;
  formatDateLabel: (value?: string | null) => string;
}

const StudentAttendanceHistoryModal: React.FC<StudentAttendanceHistoryModalProps> = ({
  open,
  studentName,
  entries,
  summary,
  onClose,
  formatDateLabel,
}) => (
  <FullScreenModal open={open} onClose={onClose} title="Frequência no trimestre" subtitle={studentName}>
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Card className="overflow-hidden border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-3">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Presenças
              </span>
              <p className="text-3xl font-bold leading-none text-slate-900">{summary.present}</p>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 ring-1 ring-slate-200">
              <CircleCheck size={20} />
            </div>
          </div>
        </Card>
        <Card className="overflow-hidden border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-3">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Ausências
              </span>
              <p className="text-3xl font-bold leading-none text-slate-900">{summary.absent}</p>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 ring-1 ring-slate-200">
              <CircleAlert size={20} />
            </div>
          </div>
        </Card>
      </div>

      {entries.length > 0 ? (
        entries.map((entry) => (
          <div key={entry.id} className="rounded-3xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">{entry.lessonTitle}</p>
                <p className="mt-1 text-sm text-slate-500">
                  {entry.lessonNumber ? `Lição ${entry.lessonNumber} • ` : ''}
                  {formatDateLabel(entry.date)}
                </p>
              </div>
              <span
                className={cn(
                  'rounded-full px-3 py-1.5 text-xs font-semibold',
                  entry.status === 'present' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                )}
              >
                {entry.status === 'present' ? 'Presente' : 'Ausente'}
              </span>
            </div>

            <div className="mt-3 rounded-2xl bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Resumo da aula</p>
              <p className="mt-1 text-sm text-slate-700">
                {entry.presentCount}/{entry.enrolledCount} alunos presentes na chamada.
              </p>
              {entry.notes && <p className="mt-2 text-sm text-slate-600">{entry.notes}</p>}
            </div>
          </div>
        ))
      ) : (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
          Este aluno ainda não possui registros de frequência no trimestre atual.
        </div>
      )}
    </div>
  </FullScreenModal>
);

export default StudentAttendanceHistoryModal;
